import React from 'react'
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import ListService from 'api/services/Lists'
import { useForm, Controller, SubmitHandler } from 'react-hook-form'
import { Check, Close, EditOutlined } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { AdminPropertyAd } from 'types'
import { PropertyAttributeEnum } from 'types/enums'

interface FormFields {
  name: string
  oldName: string
}

interface Props {
  name: string
  notes: string | null
  propertyAdAlternativeId: string | null
  listId: number | null
  onUpdate: (
    name: string | null,
    notes: string | null,
    propertyAdAlternativeId: string | null
  ) => void
  propertyAd: AdminPropertyAd
  matchesSM: boolean
}
function ListingAlias(props: Props) {
  const {
    name,
    notes,
    propertyAdAlternativeId,
    listId,
    onUpdate,
    propertyAd,
    matchesSM,
  } = props
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  console.log('props', props)

  React.useEffect(() => {
    reset({
      name: name ?? '',
      oldName: name ?? '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name])

  const updateListMutation = ListService.hooks.useAddToUserFavoritesList({
    onError: () => {
      enqueueSnackbar(t('error.unspecific'), {
        variant: 'error',
      })
    },
    onSuccess: (_data, { name }) => {
      reset({ name: name ?? '', oldName: name ?? '' })
      setEditable(false)
      enqueueSnackbar(t('ListPage.messages.success'), {
        variant: 'success',
      })
      onUpdate(name ?? '', notes, propertyAdAlternativeId)
    },
  })

  const [editable, setEditable] = React.useState(false)

  const handleMakeEditable = () => {
    setEditable(true)
    reset({
      name: name ?? '',
      oldName: name ?? '',
    })
  }

  const handleClose = () => {
    setEditable(false)
    reset({
      name: name ?? '',
      oldName: name ?? '',
    })
  }

  const handleMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
  }

  const { control, handleSubmit, reset } = useForm<FormFields>({
    defaultValues: {
      name: '',
      oldName: '',
    },
  })

  const onSubmit: SubmitHandler<FormFields> = (data) => {
    if (data.name !== data.oldName && propertyAdAlternativeId) {
      updateListMutation.mutate({
        listId: listId ?? null,
        propertyAdAlternativeId: propertyAdAlternativeId,
        name: data.name,
        notes,
      })
    } else {
      setEditable(false)
    }
  }

  const PROPERTY_TYPE = React.useMemo(() => {
    return propertyAd?.properties?.[0]?.propertyAttributes?.find((item) =>
      [1, 4].includes(propertyAd.adCategory.id)
        ? item.type.description === PropertyAttributeEnum.ESTATE_PROPERTY_TYPE
        : [2, 5].includes(propertyAd.adCategory.id)
        ? item.type.description ===
          PropertyAttributeEnum.ESTATE_PROPERTY_COMMERCIAL_TYPE
        : item.type.description ===
          PropertyAttributeEnum.ESTATE_PROPERTY_LAND_TYPE
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (matchesSM) {
    return (
      <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>
        {name
          ? name
          : `${t(
              `enums.${PROPERTY_TYPE?.type.description}.${PROPERTY_TYPE?.valueText}.base`
            )} ${
              propertyAd.properties?.[0]?.squareMeters
                ? t('generics.area', {
                    count: Number(
                      propertyAd?.properties?.[0]?.squareMeters.toFixed(2)
                    ),
                  })
                : '-'
            }`}
      </Typography>
    )
  }
  return (
    <>
      <Controller
        name="name"
        control={control}
        rules={{
          maxLength: {
            value: 100,
            message: t('ListPage.messages.inputMaxLength'),
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <FormControl>
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <TextField
                placeholder={`${t(
                  `enums.${PROPERTY_TYPE?.type.description}.${PROPERTY_TYPE?.valueText}.base`
                )} ${
                  propertyAd.properties?.[0]?.squareMeters
                    ? t('generics.area', {
                        count: Number(
                          propertyAd?.properties?.[0]?.squareMeters.toFixed(2)
                        ),
                      })
                    : '-'
                }`}
                variant="standard"
                fullWidth
                error={!!error}
                {...field}
                sx={{
                  input: {
                    '&::placeholder': {
                      opacity: 1,
                    },
                  },
                  '& .MuiInput-root': {
                    fontWeight: 700,
                    fontSize: '18px',
                  },
                }}
                helperText={
                  error ? (
                    <Typography
                      color="textPrimary"
                      sx={{ fontWeight: 500, fontSize: '14px' }}
                    >
                      {error?.message}
                    </Typography>
                  ) : null
                }
                InputProps={{
                  readOnly: !editable,
                  disableUnderline: !editable,
                  endAdornment: (
                    <InputAdornment position="end">
                      {editable ? (
                        <IconButton
                          onClick={() => handleSubmit(onSubmit)()}
                          onMouseDown={handleMouseDown}
                          edge="end"
                        >
                          <Check sx={{ color: 'success.main' }} />
                        </IconButton>
                      ) : null}
                      <IconButton
                        onClick={handleMakeEditable}
                        onMouseDown={handleMouseDown}
                        edge="end"
                      >
                        {editable ? null : <EditOutlined />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {editable ? (
                <IconButton
                  size="small"
                  onClick={handleClose}
                  onMouseDown={handleMouseDown}
                  sx={{ mb: error ? '25px' : '0px', ml: '8px' }}
                >
                  <Close color="action" />
                </IconButton>
              ) : null}
            </Box>
          </FormControl>
        )}
      />
    </>
  )
}

export default ListingAlias
